<script setup lang="ts">
import { computed } from 'vue'
import type { Finding } from '@/lib/usePolledScan'
import ToneChip from './ToneChip.vue'
import ToneGlyphIcon from './ToneGlyph.vue'

const props = withDefaults(
  defineProps<{
    findings: readonly Finding[]
    open?: boolean
  }>(),
  { open: false },
)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

// "1 checks passed" reads wrong, so the count gets its own singular case.
const label = computed(() => {
  const count = props.findings.length
  return count === 1 ? '1 check passed' : `${count} checks passed`
})

function onToggle(event: Event) {
  emit('update:open', (event.target as HTMLDetailsElement).open)
}
</script>

<template>
  <details
    class="card overflow-hidden"
    :open="props.open"
    @toggle="onToggle"
  >
    <summary class="tap flex cursor-pointer items-center gap-2 p-3 select-none">
      <ToneChip
        tone="pass"
        glyph="check"
        :label="label"
      />
      <svg
        class="chevron ml-auto transition-transform duration-150 motion-reduce:transition-none"
        width="12"
        height="12"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M6 4 L10 8 L6 12" />
      </svg>
    </summary>
    <ul
      v-if="props.open"
      class="flex flex-col gap-1 border-t border-line p-3"
    >
      <li
        v-for="finding in props.findings"
        :key="finding.key"
        class="flex items-center gap-2 text-sm"
      >
        <ToneGlyphIcon glyph="check" />
        <span class="break-words text-ink">{{ finding.title }}</span>
        <span class="quoted break-all">{{ finding.subject }}</span>
      </li>
    </ul>
  </details>
</template>

<style scoped>
/* The [open] boolean attribute is the state; this just reads it in CSS
   instead of driving the rotation from a :style binding. */
details[open] .chevron {
  transform: rotate(90deg);
}
</style>
